import { Checkbox, IconTile, ListItem, Skeleton } from "@design";
import { ChevronRight, File, FileSpreadsheet, FileText, Folder, Presentation } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { SharePointItem } from "@/api";
import { Text } from "@/components/Text";
import { formatBytes, formatDate } from "@/i18n/format";

const SHORT_DATE: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
const SKELETON_ROWS = 5;

function fileIcon(name: string) {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (["xls", "xlsx", "csv"].includes(ext)) return <FileSpreadsheet size={14} />;
  if (["ppt", "pptx"].includes(ext)) return <Presentation size={14} />;
  if (["pdf", "doc", "docx", "txt", "md"].includes(ext)) return <FileText size={14} />;
  return <File size={14} />;
}

/** A row that opens something (a site, a library, a folder): no checkbox, a chevron at the end. */
export function OpenRow({
  icon,
  label,
  description,
  divider,
  onOpen,
}: Readonly<{ icon: ReactNode; label: string; description?: string; divider: boolean; onOpen: () => void }>) {
  return (
    <li className="flex items-center gap-2">
      <span className="size-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <ListItem
          leading={<IconTile>{icon}</IconTile>}
          description={description}
          trailing={<ChevronRight size={16} className="text-content-tertiary" />}
          divider={divider}
          onClick={onOpen}
        >
          {label}
        </ListItem>
      </div>
    </li>
  );
}

export function FolderRow({
  item,
  divider,
  onOpen,
}: Readonly<{ item: SharePointItem; divider: boolean; onOpen: () => void }>) {
  const { t } = useTranslation("chat", { keyPrefix: "cloud" });
  return (
    <OpenRow
      icon={<Folder size={14} />}
      label={item.name}
      description={item.child_count == null ? undefined : t("folderItems", { count: item.child_count })}
      divider={divider}
      onOpen={onOpen}
    />
  );
}

/** A file with its checkbox. Files chat can't take stay listed, disabled, with the reason as their second line. */
export function FileRow({
  item,
  usable,
  checked,
  divider,
  onToggle,
}: Readonly<{
  item: SharePointItem;
  usable: boolean;
  checked: boolean;
  divider: boolean;
  onToggle: (on: boolean) => void;
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "cloud" });
  const detail = [
    item.last_modified ? t("edited", { date: formatDate(item.last_modified, SHORT_DATE) }) : null,
    item.size == null ? null : formatBytes(item.size),
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <li
      aria-disabled={!usable || undefined}
      className={["flex items-center gap-2", usable ? "" : "opacity-50"].join(" ")}
    >
      <Checkbox aria-label={item.name} checked={checked} disabled={!usable} onChange={onToggle} />
      <div className="min-w-0 flex-1">
        <ListItem
          leading={<IconTile>{fileIcon(item.name)}</IconTile>}
          description={usable ? detail || undefined : t("unsupported")}
          divider={divider}
        >
          {item.name}
        </ListItem>
      </div>
    </li>
  );
}

export function RowsSkeleton() {
  const { t } = useTranslation("chat", { keyPrefix: "cloud" });
  return (
    <ul aria-busy="true" aria-label={t("loading")} className="flex flex-col">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows
        <li key={i} className="flex items-center gap-3 py-3">
          <Skeleton variant="block" className="size-8" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className={i % 2 ? "h-3 w-1/2" : "h-3 w-2/3"} />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function NoMatches({ title }: Readonly<{ title: string }>) {
  const { t } = useTranslation("chat", { keyPrefix: "cloud" });
  return (
    <div className="flex flex-col items-center gap-1 py-12 text-center">
      <Text variant="body">{title}</Text>
      <Text variant="caption" tone="secondary">
        {t("noMatchesHint")}
      </Text>
    </div>
  );
}
